import React, { useState, useEffect } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import BusinessIcon from '@mui/icons-material/Business'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PhoneIcon from '@mui/icons-material/Phone'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { toast } from 'react-toastify'
import { styled } from '@mui/material/styles'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, platformWeb } from 'src/consumer/services'
import dayjs from 'dayjs'
import { getUserSignInId } from 'src/shared/utils/accountIdentity'

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: '1px solid rgba(116, 80, 170, 0.1)',
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%)',
}))

const DrawerContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  flex: 1,
}))

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  border: '1px solid rgba(116, 80, 170, 0.1)',
  boxShadow: '0 2px 8px rgba(116, 80, 170, 0.08)',
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 700,
  color: '#7450aa',
  marginBottom: theme.spacing(1.5),
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: '8px',
  background: 'rgba(116, 80, 170, 0.02)',
}))

const PermissionChip = styled(Chip)(({ theme, allowed }) => ({
  height: '24px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: allowed
    ? 'rgba(16, 185, 129, 0.12)'
    : 'rgba(239, 68, 68, 0.12)',
  color: allowed ? '#059669' : '#dc2626',
  border: `1px solid ${allowed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
}))

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusColors = {
    APPROVED: { bg: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: 'rgba(16, 185, 129, 0.3)' },
    PENDING: { bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: 'rgba(245, 158, 11, 0.3)' },
    REJECTED: { bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.3)' },
  }
  const colors = statusColors[status] || statusColors.PENDING
  return {
    height: '26px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
  }
})

export default function ClinicAssistantProfileDrawer({ open, onClose, clinicId, assistantData }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [profileData, setProfileData] = useState(null)
  const [clinicProfileData, setClinicProfileData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [managerInfo, setManagerInfo] = useState({}) // Map of managerId -> { name, role }

  useEffect(() => {
    if (open) {
      fetchProfileData()
      if (clinicId) {
        fetchClinicProfileData(clinicId)
      }
    }
  }, [open, clinicId])

  // Fetch manager names when clinic profile data is available
  useEffect(() => {
    if (clinicProfileData?.supportRoles && clinicProfileData.supportRoles.length > 0) {
      fetchManagerNames(clinicProfileData.supportRoles)
    }
  }, [clinicProfileData])

  // Fix aria-hidden accessibility issue
  // When drawer is open and has focusable content, ensure backdrop doesn't have aria-hidden
  useEffect(() => {
    if (!open) return

    // Use a small delay to ensure MUI has finished rendering the drawer
    const timer = setTimeout(() => {
      // Find the drawer modal root by searching for the most recently opened drawer
      const drawers = document.querySelectorAll('.MuiDrawer-root')
      const openDrawer = Array.from(drawers).find((drawer) => {
        const modalRoot = drawer.closest('.MuiModal-root')
        return modalRoot && modalRoot.style.display !== 'none'
      })

      if (openDrawer) {
        const modalRoot = openDrawer.closest('.MuiModal-root')
        if (modalRoot) {
          // Check if drawer has focusable content
          const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper')
          const hasFocusableContent = drawerPaper?.querySelector(
            'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )

          // If drawer has focusable content, remove aria-hidden from backdrop
          if (hasFocusableContent) {
            // Remove aria-hidden from modal root
            modalRoot.removeAttribute('aria-hidden')

            // Find and fix backdrop
            const backdrop = modalRoot.querySelector('.MuiBackdrop-root')
            if (backdrop) {
              backdrop.removeAttribute('aria-hidden')
            }
          }
        }
      }
    }, 100)

    return () => {
      clearTimeout(timer)
    }
  }, [open])

  const fetchProfileData = async () => {
    setLoading(true)
    try {
      const response = await getCPDataWithoutParams('/api/secure/clinic-assistant/profile/self')
      if (response?.status === 200 && response?.data?.data?.['clinic-assistant-profile']) {
        setProfileData(response.data.data['clinic-assistant-profile'])
      }
    } catch (error) {
      console.error('Error fetching profile data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClinicProfileData = async (clinicId) => {
    try {
      const response = await getCPDataWithoutParams(
        `/api/secure/clinic-assistant/profile?clinicId=${clinicId}`,
      )
      if (response?.status === 200 && response?.data?.data?.assistant) {
        setClinicProfileData(response.data.data.assistant)
      }
    } catch (error) {
      console.error('Error fetching clinic profile data:', error)
    }
  }

  // Helper function to determine role from categories
  const getManagerRole = (categories) => {
    if (!categories || !Array.isArray(categories)) return null
    if (categories.includes('doctor')) return 'Doctor'
    if (categories.includes('therapist')) return 'Therapist'
    if (categories.includes('screener')) return 'Screener'
    return null
  }

  const fetchManagerNames = async (supportRoles) => {
    if (!supportRoles || supportRoles.length === 0) return

    try {
      const timezoneOffset = getTimezoneOffset() || '+05:30'
      const managerPromises = supportRoles
        .filter((role) => role?.managerId)
        .map(async (role) => {
          try {
            const response = await getCPDataWithoutParams(
              `/api/secure/clinic-assistant/service-provider?id=${
                role.managerId
              }&slug=&zoneOffset=${encodeURIComponent(
                timezoneOffset,
              )}&appVersion=${appVersion}&platformType=${platformWeb}`,
            )
            if (response?.status === 200 && response?.data) {
              const managerData = response.data
              const roleName = getManagerRole(managerData?.categories)
              return {
                managerId: role.managerId,
                name: managerData?.name || managerData?.firstName || role.managerId,
                role: roleName,
              }
            }
            return {
              managerId: role.managerId,
              name: role.managerId, // Fallback to ID if fetch fails
              role: null,
            }
          } catch (error) {
            console.error(`Error fetching manager ${role.managerId}:`, error)
            return {
              managerId: role.managerId,
              name: role.managerId, // Fallback to ID on error
              role: null,
            }
          }
        })

      const results = await Promise.all(managerPromises)
      const infoMap = {}
      results.forEach((result) => {
        if (result) {
          infoMap[result.managerId] = {
            name: result.name,
            role: result.role,
          }
        }
      })
      setManagerInfo(infoMap)
    } catch (error) {
      console.error('Error fetching manager names:', error)
    }
  }

  // Use provided assistantData if available, otherwise use fetched data
  const userProfile = assistantData?.userProfileData || null
  const profile = profileData || null
  const clinicProfile = clinicProfileData || assistantData?.clinicAssistantProfileData?.find(
    (item) => item?.clinicId === clinicId,
  ) || null

  const displayName = userProfile?.name || userProfile?.firstName || profile?.name || 'Clinic Assistant'
  const signInId = getUserSignInId(userProfile || profile)

  // Get clinic brand names from assistantData
  const getClinicBrandName = (brandId) => {
    if (!brandId || !assistantData?.associatedClinicsData) return brandId
    const clinic = assistantData.associatedClinicsData.find((c) => c?.clinicBrandId === brandId)
    return clinic?.clinicBrandName || clinic?.brandName || brandId
  }

  // Get clinic name from assistantData
  const getClinicName = (clinicId) => {
    if (!clinicId || !assistantData?.associatedClinicsData) return clinicId
    const clinic = assistantData.associatedClinicsData.find((c) => c?.id === clinicId)
    return clinic?.name || clinic?.title || clinic?.clinicLocationName || clinicId
  }

  // Copy profile ID to clipboard
  const handleCopyProfileId = () => {
    if (profile?.id) {
      navigator.clipboard.writeText(profile.id)
      toast.success('Profile ID copied to clipboard')
    }
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 600,
          maxWidth: isMobile ? '100%' : '90vw',
          maxHeight: isMobile ? '90vh' : '100vh',
          borderTopLeftRadius: isMobile ? theme.shape.borderRadius * 2 : 0,
          borderTopRightRadius: isMobile ? theme.shape.borderRadius * 2 : 0,
          boxShadow: isMobile
            ? '0 -4px 20px rgba(0, 0, 0, 0.15)'
            : '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      }}
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook if needed
        disableEnforceFocus: false, // Allow focus management
        disableAutoFocus: false, // Allow auto-focus on open
        hideBackdrop: false,
      }}
    >
      <DrawerHeader>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#7450aa' }}>
          Profile Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DrawerHeader>

      <DrawerContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={40} sx={{ color: '#7450aa' }} />
          </Box>
        ) : (
          <>
            {/* Profile Overview */}
            <SectionCard>
              <CardContent>
                <SectionTitle>Profile Overview</SectionTitle>
                <InfoRow>
                  <PersonIcon sx={{ color: '#7450aa', fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Name
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {displayName}
                      </Typography>
                      {profile?.id && (
                        <Tooltip title="Copy Profile ID">
                          <IconButton
                            size="small"
                            onClick={handleCopyProfileId}
                            sx={{
                              padding: '4px',
                              color: '#7450aa',
                              '&:hover': {
                                backgroundColor: 'rgba(116, 80, 170, 0.1)',
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </InfoRow>

                <InfoRow>
                  <LockIcon sx={{ color: '#7450aa', fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                      Sign-in ID
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {signInId || 'Not specified'}
                    </Typography>
                  </Box>
                </InfoRow>

                {profile?.kycStatus && (
                  <InfoRow>
                    <CheckCircleIcon sx={{ color: '#7450aa', fontSize: 20 }} />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                          KYC Status
                        </Typography>
                        <StatusChip label={profile.kycStatus} status={profile.kycStatus} size="small" />
                      </Box>
                      {profile.approvedTime && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>
                          {dayjs(profile.approvedTime).format('MMM DD, YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </InfoRow>
                )}

                {profile?.clinicBrandIds && profile.clinicBrandIds.length > 0 && (
                  <InfoRow>
                    <BusinessIcon sx={{ color: '#7450aa', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px', mb: 0.5 }}>
                        Approved Clinic Brands
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {profile.clinicBrandIds.map((brandId, index) => {
                          const brandName = getClinicBrandName(brandId)
                          return (
                            <Chip
                              key={index}
                              label={brandName}
                              size="small"
                              sx={{
                                height: '22px',
                                fontSize: '10px',
                                backgroundColor: 'rgba(116, 80, 170, 0.1)',
                                color: '#7450aa',
                              }}
                            />
                          )
                        })}
                      </Box>
                    </Box>
                  </InfoRow>
                )}
              </CardContent>
            </SectionCard>

            {/* Clinic-Specific Permissions */}
            {clinicProfile && clinicId && (
              <SectionCard>
                <CardContent>
                  <SectionTitle>Clinic Permissions</SectionTitle>

                  {clinicProfile.clinicId && (
                    <InfoRow>
                      <LocationOnIcon sx={{ color: '#7450aa', fontSize: 20 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                          Clinic Location
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {getClinicName(clinicProfile.clinicId)}
                        </Typography>
                      </Box>
                    </InfoRow>
                  )}

                  {/* Patient Access Permissions */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                      Patient Access
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '8px',
                            border: `1px solid ${
                              clinicProfile.accessPatientDetails
                                ? 'rgba(16, 185, 129, 0.2)'
                                : 'rgba(239, 68, 68, 0.2)'
                            }`,
                            backgroundColor: clinicProfile.accessPatientDetails
                              ? 'rgba(16, 185, 129, 0.05)'
                              : 'rgba(239, 68, 68, 0.05)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {!clinicProfile.accessPatientDetails && (
                                <LockIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                              )}
                              <Typography variant="body2" fontWeight={600}>
                                View Patient Details
                              </Typography>
                            </Box>
                            <PermissionChip
                              label={clinicProfile.accessPatientDetails ? 'Allowed' : 'Restricted'}
                              allowed={clinicProfile.accessPatientDetails}
                              size="small"
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>
                            {clinicProfile.accessPatientDetails
                              ? 'You can view patient information'
                              : "Your account doesn't allow viewing patient details"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '8px',
                            border: `1px solid ${
                              clinicProfile.accessPatientContact
                                ? 'rgba(16, 185, 129, 0.2)'
                                : 'rgba(239, 68, 68, 0.2)'
                            }`,
                            backgroundColor: clinicProfile.accessPatientContact
                              ? 'rgba(16, 185, 129, 0.05)'
                              : 'rgba(239, 68, 68, 0.05)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {!clinicProfile.accessPatientContact && (
                                <LockIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                              )}
                              <PhoneIcon sx={{ fontSize: 16, color: '#7450aa' }} />
                              <Typography variant="body2" fontWeight={600}>
                                View Patient Contact
                              </Typography>
                            </Box>
                            <PermissionChip
                              label={clinicProfile.accessPatientContact ? 'Allowed' : 'Restricted'}
                              allowed={clinicProfile.accessPatientContact}
                              size="small"
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>
                            {clinicProfile.accessPatientContact
                              ? 'You can view patient phone numbers'
                              : "Your account doesn't allow viewing patient phone numbers"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Additional Permissions */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                      Additional Permissions
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      <ListItem sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <VisibilityIcon sx={{ fontSize: 18, color: '#7450aa' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="View Patient History"
                          secondary={
                            clinicProfile.canViewPatientHistory
                              ? 'You can access patient treatment history'
                              : 'You cannot access patient treatment history'
                          }
                          primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '11px' }}
                        />
                        <PermissionChip
                          label={clinicProfile.canViewPatientHistory ? 'Yes' : 'No'}
                          allowed={clinicProfile.canViewPatientHistory}
                          size="small"
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CalendarTodayIcon sx={{ fontSize: 18, color: '#7450aa' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="View Joining Details"
                          secondary={
                            clinicProfile.canViewJoiningDetails
                              ? 'You can view video session joining links'
                              : 'You cannot view video session joining links'
                          }
                          primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '11px' }}
                        />
                        <PermissionChip
                          label={clinicProfile.canViewJoiningDetails ? 'Yes' : 'No'}
                          allowed={clinicProfile.canViewJoiningDetails}
                          size="small"
                        />
                      </ListItem>
                    </List>
                  </Box>

                  {/* Support Roles */}
                  {clinicProfile.supportRoles && clinicProfile.supportRoles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: 'text.primary' }}>
                        Support Roles
                      </Typography>
                      {clinicProfile.supportRoles.map((role, index) => (
                        <Card
                          key={index}
                          sx={{
                            mb: 1,
                            border: '1px solid rgba(116, 80, 170, 0.15)',
                            backgroundColor: 'rgba(116, 80, 170, 0.02)',
                          }}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip
                                label={role.roleId || 'Support Role'}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(116, 80, 170, 0.15)',
                                  color: '#7450aa',
                                  fontWeight: 600,
                                  fontSize: '10px',
                                }}
                              />
                              {role.managerId && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon sx={{ fontSize: 14, color: '#7450aa' }} />
                                    <Typography variant="caption" sx={{ color: 'text.primary', fontSize: '11px', fontWeight: 500 }}>
                                      {managerInfo[role.managerId]?.name || role.managerId}
                                    </Typography>
                                  </Box>
                                  {managerInfo[role.managerId]?.role && (
                                    <Chip
                                      label={managerInfo[role.managerId].role}
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '9px',
                                        fontWeight: 600,
                                        backgroundColor:
                                          managerInfo[role.managerId].role === 'Doctor'
                                            ? 'rgba(59, 130, 246, 0.12)'
                                            : managerInfo[role.managerId].role === 'Therapist'
                                            ? 'rgba(16, 185, 129, 0.12)'
                                            : 'rgba(116, 80, 170, 0.12)',
                                        color:
                                          managerInfo[role.managerId].role === 'Doctor'
                                            ? '#3b82f6'
                                            : managerInfo[role.managerId].role === 'Therapist'
                                            ? '#059669'
                                            : '#7450aa',
                                        border: `1px solid ${
                                          managerInfo[role.managerId].role === 'Doctor'
                                            ? 'rgba(59, 130, 246, 0.3)'
                                            : managerInfo[role.managerId].role === 'Therapist'
                                            ? 'rgba(16, 185, 129, 0.3)'
                                            : 'rgba(116, 80, 170, 0.3)'
                                        }`,
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                            {role.managedEvents && role.managedEvents.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mb: 0.5 }}>
                                  Managed Events:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {role.managedEvents.map((event, idx) => (
                                    <Chip
                                      key={idx}
                                      label={event}
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '9px',
                                        backgroundColor: 'rgba(116, 80, 170, 0.1)',
                                        color: '#7450aa',
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {(role.allowedUploadTypes || role.allowedPrintTypes) && (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {role.allowedUploadTypes && role.allowedUploadTypes.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mb: 0.5 }}>
                                      Can Upload:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {role.allowedUploadTypes.map((type, idx) => (
                                        <Chip
                                          key={idx}
                                          label={type}
                                          size="small"
                                          sx={{
                                            height: '20px',
                                            fontSize: '9px',
                                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                            color: '#059669',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {role.allowedPrintTypes && role.allowedPrintTypes.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mb: 0.5 }}>
                                      Can Print:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {role.allowedPrintTypes.map((type, idx) => (
                                        <Chip
                                          key={idx}
                                          label={type}
                                          size="small"
                                          sx={{
                                            height: '20px',
                                            fontSize: '9px',
                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                            color: '#3b82f6',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </SectionCard>
            )}
          </>
        )}
      </DrawerContent>
    </Drawer>
  )
}
